import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Linkedin, Instagram, Mail } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_biz-scorecard-1/artifacts/jq536b3b_PROFITMIND%20Logo_Low%20Res_May%202024_WHITE%20SHADOW_BLACK_Yellow%20Bckgr.png";

export const BrandMark = ({ className = "", size = "default" }) => {
    const sizeCls = size === "lg" ? "w-32 sm:w-36" : "w-20";
    return (
        <a href="https://profitmind.co.uk" data-testid="brand-mark" className={`inline-flex items-center ${className}`}>
            <img src={LOGO_URL} alt="ProfitMIND" className={`${sizeCls} h-auto rounded-lg object-contain`} />
        </a>
    );
};

export const Header = () => {
    const { pathname } = useLocation();
    const hideCta = pathname.startsWith("/assessment");
    return (
        <header className="sticky top-0 z-40 bg-white border-b border-black/10" data-testid="site-header">
            <div className="px-6 sm:px-10 py-4 max-w-6xl mx-auto flex items-center justify-between">
                <BrandMark size="lg" />
                {!hideCta && (
                    <Link to="/assessment" data-testid="header-cta" className="hidden sm:inline-flex btn-pm text-xs sm:text-sm">
                        Get Your Reality Check <span className="ml-1">→</span>
                    </Link>
                )}
            </div>
        </header>
    );
};

const SocialIcon = ({ href, label, children, ...rest }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className="inline-flex w-10 h-10 rounded-lg border-2 border-white text-white hover:bg-white hover:text-black transition-colors items-center justify-center"
        {...rest}
    >
        {children}
    </a>
);

const FooterLink = ({ href, children, testid, external = true }) => (
    <a
        href={href}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        data-testid={testid}
        className="text-black/85 hover:text-black hover:underline text-sm leading-relaxed"
    >
        {children}
    </a>
);

export const Footer = () => (
    <footer className="bg-pm-yellow border-t-2 border-black mt-24" data-testid="site-footer">
        <div className="px-6 sm:px-10 py-14 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                {/* Col 1 — Brand + social */}
                <div>
                    <BrandMark size="lg" />
                    <div className="mt-6 flex gap-2.5" data-testid="footer-social">
                        <SocialIcon href="https://uk.linkedin.com/in/magdalena-mahdy" label="LinkedIn" data-testid="footer-linkedin">
                            <Linkedin className="w-4 h-4" />
                        </SocialIcon>
                        <SocialIcon href="https://www.instagram.com/profitminduk/" label="Instagram" data-testid="footer-instagram">
                            <Instagram className="w-4 h-4" />
                        </SocialIcon>
                        <SocialIcon href="mailto:hello@profitmind.co.uk" label="Email" data-testid="footer-email">
                            <Mail className="w-4 h-4" />
                        </SocialIcon>
                    </div>
                </div>

                {/* Col 2 — Explore */}
                <div data-testid="footer-explore">
                    <h4 className="font-display uppercase text-2xl text-white mb-4">Explore</h4>
                    <ul className="space-y-2.5">
                        <li><FooterLink href="https://profitmind.co.uk/" testid="footer-home">Home</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/about" testid="footer-about">About</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/work-with-me" testid="footer-work-with-me">Work With Me</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/follow-the-cash" testid="footer-follow-the-cash">Follow The Cash</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/contact" testid="footer-contact">Contact</FooterLink></li>
                    </ul>
                </div>

                {/* Col 3 — Quick links (no headline) */}
                <div data-testid="footer-quick">
                    <h4 className="font-display uppercase text-2xl text-white mb-4 opacity-0 select-none" aria-hidden>·</h4>
                    <ul className="space-y-2.5">
                        <li><FooterLink href="https://assessment.profitmind.co.uk/" testid="footer-assessment" external={false}>Free Reality Check</FooterLink></li>
                        <li><FooterLink href="https://forecastly.me" testid="footer-forecastly">forecastly</FooterLink></li>
                        <li><FooterLink href="https://schedule.profitmind.co.uk/free-20min-chat" testid="footer-bookcall">Book a call</FooterLink></li>
                    </ul>
                </div>

                {/* Col 4 — Legal */}
                <div data-testid="footer-legal">
                    <h4 className="font-display uppercase text-2xl text-white mb-4">Legal</h4>
                    <ul className="space-y-2.5">
                        <li><FooterLink href="https://profitmind.co.uk/privacy-policy" testid="footer-privacy">Privacy Policy</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/cookie-policy" testid="footer-cookie">Cookie Policy</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/terms-of-use" testid="footer-tos">Terms of Use</FooterLink></li>
                        <li><FooterLink href="https://profitmind.co.uk/terms-and-conditions" testid="footer-tc">Terms &amp; Conditions</FooterLink></li>
                    </ul>
                </div>
            </div>

            <div className="mt-12 pt-6 border-t-2 border-black/15 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <p className="text-xs text-black/75">
                    © {new Date().getFullYear()} ProfitMIND Ltd · Registered in England &amp; Wales, no. 09918255 · profitmind.co.uk
                </p>
                <p className="text-xs text-black uppercase tracking-[0.2em] font-bold text-right sm:text-right">
                    Profitable But Broke.<span className="mx-1">·</span>Follow The Cash.<span className="mx-1">·</span>Own Your Future.
                </p>
            </div>
        </div>
    </footer>
);
